import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Upload,
  X,
  Loader,
  FileText,
  RotateCcw,
  Calendar,
  Search
} from 'lucide-react'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit, startAfter } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { db, storage, COLLECTIONS, STORAGE_PATHS } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'
import toast from 'react-hot-toast'

const Blog = () => {
  const PAGE_SIZE = 50
  const [blogPosts, setBlogPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastVisible, setLastVisible] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [uploading, setUploading] = useState(false)
  const DEFAULT_AUTHOR = 'Spa~Radise Luxe team'
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    author: DEFAULT_AUTHOR,
    featuredImageUrl: '',
    isPublished: true,
    tags: []
  })
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState('')
  const [newTag, setNewTag] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB')
        return
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }
      setImageFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadImage = async (file) => {
    const timestamp = Date.now()
    const fileName = `${timestamp}_${file.name}`
    const storageRef = ref(storage, `${STORAGE_PATHS.BLOG}/${fileName}`)
    
    await uploadBytes(storageRef, file)
    const downloadURL = await getDownloadURL(storageRef)
    return downloadURL
  }

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title || !formData.content) {
      toast.error('Please fill in title and content')
      return
    }

    setUploading(true)
    try {
      let featuredImageUrl = formData.featuredImageUrl

      // Upload new image if selected
      if (imageFile) {
        featuredImageUrl = await uploadImage(imageFile)
      }

      const authorName = (formData.author && formData.author.trim()) ? formData.author.trim() : DEFAULT_AUTHOR

      const blogData = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt || formData.content.substring(0, 150) + '...',
        author: authorName,
        featuredImageUrl: featuredImageUrl || '',
        isPublished: formData.isPublished,
        tags: formData.tags || [],
        createdAt: editingPost?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        publishedAt: formData.isPublished && !editingPost?.publishedAt ? new Date().toISOString() : editingPost?.publishedAt || null
      }

      if (editingPost) {
        // Delete old image if new one uploaded
        if (imageFile && editingPost.featuredImageUrl) {
          try {
            const oldImageRef = ref(storage, editingPost.featuredImageUrl)
            await deleteObject(oldImageRef)
          } catch (error) {
            console.error('Error deleting old image:', error)
          }
        }

        // Update publishedAt if publishing for the first time
        if (formData.isPublished && !editingPost.publishedAt) {
          blogData.publishedAt = new Date().toISOString()
        }

        await updateDoc(doc(db, COLLECTIONS.BLOG_POSTS, editingPost.id), blogData)
        toast.success('Blog post updated successfully!')
        
        setBlogPosts(prev => prev.map(post => 
          post.id === editingPost.id 
            ? { ...blogData, id: editingPost.id }
            : post
        ))
      } else {
        const docRef = await addDoc(collection(db, COLLECTIONS.BLOG_POSTS), blogData)
        toast.success('Blog post created successfully!')
        
        setBlogPosts(prev => [...prev, { ...blogData, id: docRef.id }])
      }

      // Reset form
      setShowAddModal(false)
      setEditingPost(null)
      setFormData({
        title: '',
        content: '',
        excerpt: '',
        author: DEFAULT_AUTHOR,
        featuredImageUrl: '',
        isPublished: true,
        tags: []
      })
      setImageFile(null)
      setImagePreview('')
    } catch (error) {
      console.error('Error saving blog post:', error)
      toast.error('Failed to save blog post. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const getDisplayAuthor = (author) => {
    const a = (author || '').trim()
    if (!a || /kparasiteprise|kparasite/i.test(a)) return DEFAULT_AUTHOR
    return a
  }

  const handleEdit = (post) => {
    setEditingPost(post)
    setFormData({
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      author: getDisplayAuthor(post.author) || DEFAULT_AUTHOR,
      featuredImageUrl: post.featuredImageUrl || '',
      isPublished: post.isPublished !== undefined ? post.isPublished : false,
      tags: post.tags || []
    })
    setImagePreview(post.featuredImageUrl || '')
    setImageFile(null)
    setShowAddModal(true)
  }

  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return
    }

    try {
      // Delete image from storage
      if (imageUrl) {
        try {
          const imageRef = ref(storage, imageUrl)
          await deleteObject(imageRef)
        } catch (error) {
          console.error('Error deleting image:', error)
        }
      }

      // Delete document from Firestore
      await deleteDoc(doc(db, COLLECTIONS.BLOG_POSTS, id))
      toast.success('Blog post deleted successfully!')
      
      setBlogPosts(prev => prev.filter(post => post.id !== id))
    } catch (error) {
      console.error('Error deleting blog post:', error)
      toast.error('Failed to delete blog post. Please try again.')
    }
  }

  const fetchBlogPosts = async (cursor = null, append = false) => {
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)
      const base = collection(db, COLLECTIONS.BLOG_POSTS)
      const q = cursor
        ? query(base, orderBy('createdAt', 'desc'), limit(PAGE_SIZE), startAfter(cursor))
        : query(base, orderBy('createdAt', 'desc'), limit(PAGE_SIZE))
      const snapshot = await getDocs(q)
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
      setLastVisible(lastDoc)
      setHasMore(snapshot.docs.length === PAGE_SIZE)
      if (append) setBlogPosts(prev => [...prev, ...posts])
      else setBlogPosts(posts)
    } catch (error) {
      console.error('Error fetching blog posts:', error)
      toast.error('Failed to load blog posts. Please try again.')
      setHasMore(false)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }

  const loadMoreBlogPosts = () => {
    if (!lastVisible || loadingMore) return
    fetchBlogPosts(lastVisible, true)
  }

  useEffect(() => {
    fetchBlogPosts()
  }, [])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const filteredPosts = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase()
    if (!q) return blogPosts
    return blogPosts.filter(post => {
      const match = (s) => (s || '').toLowerCase().includes(q)
      return match(post.title) || match(post.excerpt) || match(post.content) || match(post.author) || (post.tags || []).some(t => match(String(t)))
    })
  }, [blogPosts, searchQuery])

  return (
    <div className="space-y-6 text-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Blog Management</h1>
          <p className="text-gray-300 mt-1">Manage your blog posts and articles</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={() => fetchBlogPosts()}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => {
              setShowAddModal(true)
              setEditingPost(null)
              setFormData({
                title: '',
                content: '',
                excerpt: '',
                author: DEFAULT_AUTHOR,
                featuredImageUrl: '',
                isPublished: true,
                tags: []
              })
              setImageFile(null)
              setImagePreview('')
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Post</span>
          </button>
        </div>
      </div>

      <PageHelpButton
        videoId={7}
        title="Blog management tutorial"
        youtubeUrl="https://www.youtube.com/embed/5zktMncxHNM"
      />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by title, excerpt, author, tags..."
          className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500"
        />
        {searchQuery.trim() && <span className="text-xs text-gray-400 ml-2">{filteredPosts.length} result(s)</span>}
      </div>

      {/* Blog Posts Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg">
          <FileText className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No blog posts yet. Create your first post!</p>
        </div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase w-20">Edit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Post</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Author</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase w-20">Delete</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-750">
                      <td className="px-2 py-4">
                        <button type="button" onClick={() => handleEdit(post)} className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white touch-manipulation" title="Edit"><Edit className="w-5 h-5" /></button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {post.featuredImageUrl && (
                            <img src={post.featuredImageUrl} alt="" className="w-16 h-16 object-cover rounded" onError={e => { e.target.style.display = 'none' }} />
                          )}
                          <div>
                            <div className="text-sm font-medium text-white">{post.title}</div>
                            <div className="text-xs text-gray-400 line-clamp-1">{post.excerpt || post.content.substring(0, 50)}...</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{getDisplayAuthor(post.author)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${post.isPublished ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}>{post.isPublished ? 'Published' : 'Draft'}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-2 py-4">
                        <button type="button" onClick={() => handleDelete(post.id, post.featuredImageUrl)} className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white touch-manipulation" title="Delete"><Trash2 className="w-5 h-5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {!loading && blogPosts.length > 0 && (
            <div className="border-t border-gray-700 px-6 py-3 flex items-center justify-between bg-gray-800/50">
              <p className="text-xs text-gray-400">Load more only when needed to reduce reads.</p>
              <button
                type="button"
                onClick={loadMoreBlogPosts}
                disabled={!hasMore || loadingMore}
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : hasMore ? (
                  <span>Load previous posts</span>
                ) : (
                  <span>No more</span>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal - portaled so header is below top bar */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-start justify-center p-4 pt-20 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full my-auto max-h-[calc(100vh-5rem)] flex flex-col overflow-hidden border border-gray-700">
            <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">
                {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setEditingPost(null)
                  setImageFile(null)
                  setImagePreview('')
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Featured Image
                </label>
                {imagePreview && (
                  <div className="mb-4 relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-400 mt-1">Max size: 5MB. Supported formats: JPG, PNG, WebP</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Enter blog post title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Excerpt
                </label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Short excerpt (will be auto-generated from content if left empty)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Content *
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                  rows={12}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none font-mono text-sm"
                  placeholder="Write your blog post content here..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Author
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Author (defaults to Spa~Radise Luxe team if left empty)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddTag()
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="Add a tag and press Enter"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    Add
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-full text-sm"
                      >
                        <span>{tag}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-gray-300"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isPublished"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="isPublished" className="ml-2 text-sm text-gray-300">
                  Publish (visible on main site)
                </label>
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>{editingPost ? 'Update' : 'Create'} Post</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    setEditingPost(null)
                    setImageFile(null)
                    setImagePreview('')
                  }}
                  className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Blog

